import { DailyContent } from '@/types';
import { config } from '@/config';

class CacheService {
  private readonly cacheKey = config.cache.key;
  private readonly cacheDuration = config.cache.duration;

  set(content: DailyContent): void {
    try {
      const cacheData = {
        content,
        timestamp: Date.now()
      };
      localStorage.setItem(this.cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('缓存保存失败:', error);
    }
  }

  get(): DailyContent | null {
    try {
      const cached = localStorage.getItem(this.cacheKey);
      if (!cached) return null;

      const cacheData = JSON.parse(cached);
      
      // 检查是否过期
      if (this.isExpired(cacheData.timestamp)) {
        this.clear();
        return null;
      }

      // 检查是否是同一天
      if (!this.isSameDay(cacheData.content.date)) {
        this.clear();
        return null;
      }

      return cacheData.content;
    } catch (error) {
      console.warn('缓存读取失败:', error);
      this.clear();
      return null;
    }
  }

  clear(): void {
    try {
      localStorage.removeItem(this.cacheKey);
    } catch (error) {
      console.warn('缓存清除失败:', error);
    }
  }

  isExpired(timestamp: number): boolean {
    return Date.now() - timestamp > this.cacheDuration;
  }

  isSameDay(dateString: string): boolean {
    try {
      const cached = new Date(dateString);
      const now = new Date();
      return cached.toDateString() === now.toDateString();
    } catch (error) {
      return false;
    }
  }

  // 获取缓存状态信息（用于调试）
  getStatus(): { exists: boolean; expired: boolean; sameDay: boolean; size: number } {
    const cached = localStorage.getItem(this.cacheKey);
    
    if (!cached) {
      return { exists: false, expired: false, sameDay: false, size: 0 };
    }

    try {
      const cacheData = JSON.parse(cached);
      return {
        exists: true,
        expired: this.isExpired(cacheData.timestamp),
        sameDay: this.isSameDay(cacheData.content.date),
        size: cached.length
      };
    } catch (error) {
      return { exists: true, expired: true, sameDay: false, size: cached.length };
    }
  }
}

export const cacheService = new CacheService();